import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CheckCircle,
  Circle,
  CircleDot,
  MessageSquare,
  RefreshCw,
  Tag,
  UserPlus,
  Zap,
} from 'lucide-react'
import { CommentItem } from './CommentItem'
import { CommentEditor } from './CommentEditor'
import type { LucideIcon } from 'lucide-react'
import { useActivitySentence } from '@/routes/features/tasks/hooks/useActivitySentence'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { useActivities, useComments } from '@/lib/db/rxdb/hooks'
import { useMembers } from '@/lib/hooks/useWorkspaceUsers'
import useLoginStore from '@/store/LoginStore'
import { cn } from '@/lib/utils'

interface UnifiedTimelineProps {
  entityType: string
  entityId: string
  envId: string
  /** Controlled view, for hosts that draw their own tabs (task discussion strip). */
  view?: 'comments' | 'activity'
  /** Hide the "Discussion" heading and view switch. */
  hideHeader?: boolean
  /** Hide the composer, when the host docks its own. */
  hideComposer?: boolean
  /** Mixed view: fold runs of 2+ activity items into one "· N changes · Show" line (design 28a). */
  foldActivity?: boolean
  /** Mixed view: show comments only, e.g. "Hide activity" (design 28a). */
  hideActivity?: boolean
}

type TimelineItem =
  | { type: 'comment'; data: any; timestamp: string | number | Date | null }
  | {
      type: 'activity'
      data: any
      timestamp: string | number | Date | null
      userDisplayName: string
    }

export function UnifiedTimeline({
  entityType,
  entityId,
  envId,
  view: controlledView,
  hideHeader = false,
  hideComposer = false,
  foldActivity = false,
  hideActivity = false,
}: UnifiedTimelineProps) {
  const { t } = useTranslation('common')
  const currentUserId = useLoginStore((state) => state.userId)
  // Discussion first (design 6a); Activity shows the full history.
  const [ownView, setView] = useState<'comments' | 'activity'>('comments')
  const view = controlledView ?? ownView

  // Query comments using RxDB
  const { comments: allCommentsFromQuery, isLoading: commentsLoading } =
    useComments({
      entityId,
      entityType,
      isDeleted: false,
    })

  // Query activities using RxDB
  const { activities: allActivities, isLoading: activitiesLoading } =
    useActivities({
      entityId,
      entityType,
      isDeleted: false,
    })

  // Get members for client-side join
  const { users: members, isLoading: membersLoading } = useMembers()

  // Create member lookup map
  const memberMap = useMemo(() => {
    const map = new Map()
    members.forEach((member) => {
      map.set(member.userId, member)
    })
    return map
  }, [members])

  // Check if queries are ready
  const isLoading = commentsLoading || activitiesLoading || membersLoading

  // Filter comments and activities
  const allComments = useMemo(() => {
    return allCommentsFromQuery.filter(
      (comment) =>
        comment.entityType === entityType &&
        comment.entityId === entityId &&
        comment.envId === envId &&
        !comment.isDeleted,
    )
  }, [allCommentsFromQuery, entityType, entityId, envId])

  const filteredActivities = useMemo(() => {
    return allActivities.filter(
      (activity) =>
        activity.entityType === entityType &&
        activity.entityId === entityId &&
        activity.envId === envId &&
        !activity.isDeleted,
    )
  }, [allActivities, entityType, entityId, envId])

  // Create unified timeline with user display names
  const timelineItems = useMemo(() => {
    const items: Array<TimelineItem> = []

    // Add top-level comments only (replies will be nested)
    const topLevelComments = allComments.filter((c) => !c.parentCommentId)
    topLevelComments.forEach((comment) => {
      items.push({
        type: 'comment',
        data: comment,
        timestamp: comment.createdOn || '',
      })
    })

    // Add activities with user display names via client-side join
    filteredActivities.forEach((activity) => {
      const isCurrentUser = activity.createdBy === currentUserId
      const member = memberMap.get(activity.createdBy)

      // Show "You" for own activities, otherwise lookup from members
      const displayName = isCurrentUser
        ? 'You'
        : member?.name || member?.email || 'Unknown User'

      items.push({
        type: 'activity',
        data: activity,
        timestamp: activity.createdOn || '',
        userDisplayName: displayName,
      })
    })

    // Sort by timestamp ascending (earliest first)
    return items.sort((a, b) => {
      if (a.timestamp && b.timestamp) {
        return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      }
      return 0
    })
  }, [allComments, filteredActivities, currentUserId, memberMap])

  const [expandedRuns, setExpandedRuns] = useState<ReadonlySet<string>>(() => new Set())
  const visibleItems =
    view === 'comments' || hideActivity ? timelineItems.filter((item) => item.type === 'comment') : timelineItems

  // Group consecutive activity into runs so a busy history reads as one line.
  type Block =
    | { kind: 'item'; item: TimelineItem }
    | { kind: 'run'; key: string; items: Array<Extract<TimelineItem, { type: 'activity' }>> }
  const blocks: Array<Block> = []
  for (const item of visibleItems) {
    const last = blocks.at(-1)
    if (foldActivity && item.type === 'activity') {
      if (last?.kind === 'run') last.items.push(item)
      else blocks.push({ kind: 'run', key: `run-${item.data.id}`, items: [item] })
    } else {
      blocks.push({ kind: 'item', item })
    }
  }
  const commentCount = allComments.filter((c) => !c.parentCommentId).length

  return (
    <section aria-label={t('timeline.discussion')}>
      {!hideHeader && (
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="flex items-baseline gap-1.5 text-[14px] font-semibold">
          {t('timeline.discussion')}
          <span className="text-xs font-medium tabular-nums text-muted-foreground">
            {isLoading ? '…' : commentCount}
          </span>
        </h3>
        <div role="radiogroup" className="flex items-center gap-0.5 rounded-lg bg-secondary p-0.5">
          {(['comments', 'activity'] as const).map((option) => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={view === option}
              onClick={() => setView(option)}
              className={cn(
                'h-6 rounded-md px-2.5 text-xs font-medium transition-colors',
                view === option ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t(`timeline.${option}`)}
            </button>
          ))}
        </div>
      </div>
      )}

      <div className="space-y-1">
        {isLoading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t('timeline.loading')}</p>
        ) : visibleItems.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {view === 'comments' || foldActivity || hideActivity ? t('timeline.noComments') : t('timeline.noActivity')}
          </p>
        ) : (
          blocks.map((block) => {
            if (block.kind === 'run') {
              const [first] = block.items
              if (block.items.length === 1 || expandedRuns.has(block.key) || !first) {
                return block.items.map((item) => (
                  <ActivityItem key={`activity-${item.data.id}`} activity={item.data} userDisplayName={item.userDisplayName} />
                ))
              }
              const lastItem = block.items[block.items.length - 1]
              return (
                <ActivityRunRow
                  key={block.key}
                  who={first.userDisplayName}
                  count={block.items.length}
                  timestamp={lastItem?.timestamp ?? first.timestamp}
                  onShow={() => setExpandedRuns((prev) => new Set(prev).add(block.key))}
                />
              )
            }
            const { item } = block
            return item.type === 'comment' ? (
              <CommentItem
                key={`comment-${item.data.id}`}
                comment={item.data}
                replies={allComments.filter((c) => c.parentCommentId === item.data.id)}
                entityType={entityType}
                entityId={entityId}
                workspaceId={envId}
                level={0}
                variant="stream"
              />
            ) : (
              <ActivityItem
                key={`activity-${item.data.id}`}
                activity={item.data}
                userDisplayName={item.userDisplayName}
              />
            )
          })
        )}

        {/* Comment editor at bottom */}
        {!hideComposer && (
          <div data-testid="comment-editor-section" className="pt-2">
            <CommentEditor entityType={entityType} entityId={entityId} onCancel={() => {}} />
          </div>
        )}
      </div>
    </section>
  )
}

/** A folded run of activity: "You · 4 changes · Show" (design 28a). */
function ActivityRunRow({
  who,
  count,
  timestamp,
  onShow,
}: {
  who: string
  count: number
  timestamp: string | number | Date | null
  onShow: () => void
}) {
  const { t } = useTranslation('common')
  const { smartTime } = useDateTimeFormatters()
  return (
    <div className="flex items-center gap-2 py-1.5 pl-1 text-[12.5px] text-muted-foreground">
      <RefreshCw className="size-3 shrink-0" />
      <span className="min-w-0 flex-1 truncate">
        {t('timeline.changesBy', { who, count })}{' '}
        <button type="button" onClick={onShow} className="font-medium text-brand hover:underline">
          {t('timeline.show')}
        </button>
      </span>
      {timestamp ? <span className="shrink-0 text-[11.5px]">{smartTime(new Date(timestamp))}</span> : null}
    </div>
  )
}

interface ActivityItemProps {
  activity: any
  userDisplayName: string
}

function ActivityItem({ activity, userDisplayName }: ActivityItemProps) {
  const formatters = useDateTimeFormatters()
  const buildSentence = useActivitySentence()
  const activityDate = activity.createdOn
    ? new Date(activity.createdOn)
    : new Date()
  const displayTime = formatters.smartTime(activityDate)
  const fullTimestamp = formatters.dateTime(activityDate, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
  const sentence = buildSentence(activity)

  // Get icon and color based on activity type
  const getActivityStyle = (
    activityType: string,
  ): { icon: LucideIcon; color: string } => {
    switch (activityType) {
      case 'created':
        // A neutral dot like other plain activity; a sparkle reads as AI (design 35).
        return { icon: CircleDot, color: 'text-muted-foreground/80' }
      case 'status_changed':
        return { icon: RefreshCw, color: 'text-blue-600 dark:text-blue-400' }
      case 'priority_changed':
        return { icon: Zap, color: 'text-orange-600 dark:text-orange-400' }
      case 'assigned':
        return { icon: UserPlus, color: 'text-purple-600 dark:text-purple-400' }
      case 'completed':
        return { icon: CheckCircle, color: 'text-green-600 dark:text-green-400' }
      case 'commented':
        return { icon: MessageSquare, color: 'text-muted-foreground' }
      case 'label_added':
        return { icon: Tag, color: 'text-muted-foreground' }
      case 'label_removed':
        return { icon: Tag, color: 'text-muted-foreground' }
      default:
        return { icon: Circle, color: 'text-muted-foreground' }
    }
  }

  const style = getActivityStyle(activity.activityType)
  const IconComponent = style.icon

  return (
    <div className="flex min-h-7 items-start gap-2 py-1 pl-1 text-[12.5px] leading-5 text-muted-foreground">
      <span className="grid h-5 w-[18px] shrink-0 place-items-center">
        <IconComponent className={`size-[13px] ${style.color}`} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="font-semibold text-foreground">{userDisplayName}</span> {sentence}
      </span>
      <time
        className="shrink-0 text-xs"
        title={fullTimestamp}
        dateTime={activityDate.toISOString()}
      >
        {displayTime}
      </time>
    </div>
  )
}
